import {
  Box,
  Button,
  DialogDescription,
  DialogTitle,
  FormItem,
  Input,
  Typography,
} from '@branch-messenger/willow-ui'
import { yupResolver } from '@hookform/resolvers/yup'
import { FC, use } from 'react'
import { useForm } from 'react-hook-form'

import { VerificationTimer } from '@/components/verification/VerificationTimer'
import { DialogContext } from '@/context'
import {
  VerifyOTPFormSchema,
  verifyOTPFormSchema,
} from '@/lib/formSchemas/verifyOTP'
import { formatPhoneNumber } from '@/lib/phone'
import {
  useSendOTPForEmailUpdate,
  useSendPhoneOTP,
  useUpdateAccountEmail,
  useUpdateAccountPhone,
} from '@/queries/registration/registration'

import { PhoneOTPModalContentProps } from '../types'
import { EmailDeepLinkModalContent } from './EmailDeepLinkModalContent'

export const PhoneOTPModalContent: FC<PhoneOTPModalContentProps> = ({
  phone,
  email,
}) => {
  const { hideDialog, showDialog } = use(DialogContext)
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm({
    resolver: yupResolver(verifyOTPFormSchema),
  })
  const { mutate: sendPhoneOTPForPhoneUpdate } = useSendPhoneOTP()
  const { mutate: sendOTPForEmailUpdate } = useSendOTPForEmailUpdate()

  const {
    mutate: updateAccountPhone,
    isPending: updatingAccountPhone,
    isError: phoneUpdateError,
  } = useUpdateAccountPhone()

  const {
    mutate: updateAccountEmail,
    isPending: updatingAccountEmail,
    isError: emailUpdateError,
  } = useUpdateAccountEmail()

  const handleResendCode = () => {
    email ? sendOTPForEmailUpdate() : sendPhoneOTPForPhoneUpdate(phone)
  }

  const onSubmit = ({ code }: VerifyOTPFormSchema) => {
    if (email) {
      updateAccountEmail(
        { otp: code, email },
        {
          onSuccess: () => {
            showDialog({
              children: <EmailDeepLinkModalContent email={email} />,
            })
          },
        }
      )
    } else {
      updateAccountPhone({ otp: code, phone })
    }
  }

  const handleCancel = () => {
    hideDialog()
  }

  const isLoading = updatingAccountEmail || updatingAccountPhone

  return (
    <Box>
      <Box $mb={4}>
        <DialogTitle>Enter the code we texted you.</DialogTitle>
      </Box>
      <Box $mb={6}>
        <DialogDescription>
          Please enter the code texted to:
          <Typography as="span" style={{ display: 'block' }} $bold>
            {formatPhoneNumber(phone)}
          </Typography>
        </DialogDescription>
      </Box>
      <Box
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        $display="flex"
        $direction="column"
        $align="initial"
        $gap={6}
      >
        <FormItem
          error={
            phoneUpdateError || emailUpdateError
              ? 'Invalid code, please check your code or tap resend for a new one.'
              : errors.code?.message
          }
        >
          <Input placeholder="Enter code" label="Code" {...register('code')} />
        </FormItem>
        <VerificationTimer onResendCode={handleResendCode} />
        <Box $display="flex" $justify="flex-end" $gap={2}>
          <Button
            type="button"
            color="tertiary"
            variant="outlined"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            disabled={!isValid || isLoading}
            loading={isLoading}
            type="submit"
          >
            Verify
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
